import { propertyTypeOptions } from "@/info/unit/property-types";
import { Filters } from "@/types/filters";
import { arrayToUrlSegment } from "./array-to-url-segment";
import { encodeLocation } from "./encode-decode-location";
import { encodeMaxAgeAndPossession, encodeValue } from "./encode-decode-value";
import { formatNumberForUrl } from "./format-number-for-url";
import { rangeToUrlSegment } from "./range-to-url-segment";

// Property type (e.g., "apartments", "villas")
export const propertyTypeMapLabelFromValue: Record<string, string> = {
	apartment: "apartments",
	house: "houses",
	villa: "villas",
	plot: "plots",
};

export const propertyTypeMapValueFromLabel: Record<string, string> = {
	apartments: "apartment",
	houses: "house",
	villas: "villa",
	plots: "plot",
};

export const subPropertyTypeLabelFromValue: Record<string, Record<string, string>> = {
	apartment: {
		a: "any",
		"a:p": "penthouse",
		"a:v": "villament",
	},
	house: {
		h: "any",
		"h:p-": "project",
		"h:i-": "independent",
	},
	villa: {
		v: "any",
		"v:s": "standalone",
		"v:1": "villa1_2",
		"v:r": "row",
	},
	plot: {
		p: "any",
		"p:p": "gatedbuilder",
		"p:g": "gatedcommunity",
		"p:i": "independent",
	},
};

export const subPropertyTypeValueFromLabel: Record<string, Record<string, string>> = {
	apartment: {
		any: "a",
		penthouse: "a:p",
		villament: "a:v",
	},
	house: {
		any: "h",
		project: "h:p-",
		independent: "h:i-",
	},
	villa: {
		any: "v",
		standalone: "v:s",
		villa1_2: "v:1",
		row: "v:r",
	},
	plot: {
		any: "p",
		gatedbuilder: "p:p",
		gatedcommunity: "p:g",
		independent: "p:i",
	},
};

export function filtersToUrl(filters: Partial<Filters>): string {
	const segments: string[] = [];

	// Bedrooms (e.g., "2bhk", "1-3bhk") - maintain URL format with bedroomsMin/bedroomsMax
	if (filters.bedrooms && filters.bedrooms.min && filters.bedrooms.max) {
		if (filters.bedrooms.min === filters.bedrooms.max) {
			segments.push(`${filters.bedrooms.min}bhk`);
		} else {
			segments.push(`${filters.bedrooms.min}bhk-${filters.bedrooms.max}bhk`);
		}
	}

	const propertyType = filters.propertyType;
	let subTypeLabel;
	if (propertyType) {
		segments.push(propertyTypeMapLabelFromValue[propertyType]);

		let subType = filters.subType;
		if (!subType) {
			subType = propertyTypeOptions.find(type => type.value === propertyType)?.options?.[0]?.value;
		}
		if (subType) subTypeLabel = subPropertyTypeLabelFromValue[propertyType]?.[subType];
	}

	// Locations (e.g., "in-koramangala-hsrLayout")
	if (filters.locations && filters.locations.length > 0) {
		const locSegment = filters.locations
			.map(loc => encodeLocation(loc.name + (loc.type === "project" ? "_project" : "")))
			.join("-");
		segments.push(`in-${locSegment}`);
	}

	// 1.5bhk-3bhk-apartments-in-koramangala-hsrLayout
	// --------- Add all SEO-relevant slugs above (derived from filters for URL optimization) ---------
	// --------- Below this point, add slugs purely used for filtering functionality ---------

	// Facing
	if (filters.facing?.length) {
		segments.push(arrayToUrlSegment(filters.facing.map(encodeValue), "facing"));
	}

	if (propertyType !== "apartment" && filters.plotFacing?.length) {
		segments.push(arrayToUrlSegment(filters.plotFacing.map(encodeValue), "plotfacing"));
	}

	// Built-up Area
	if (filters.builtupAreaMin && filters.builtupAreaMin !== 400) {
		segments.push(`builtuparea-${formatNumberForUrl(filters.builtupAreaMin)}Plus`);
	}

	// Plot Area
	if (propertyType !== "apartment" && filters.plotAreaMin) {
		segments.push(`plotarea-${formatNumberForUrl(filters.plotAreaMin)}Plus`);
	}

	// Budget
	if (
		filters.budget?.value &&
		filters.budget?.min &&
		filters.budget?.max &&
		filters.budget.value > 0 &&
		filters.budget.min > 0 &&
		filters.budget.max > 0
	) {
		segments.push(
			"budget",
			`min-${formatNumberForUrl(filters.budget.min)}-val-${formatNumberForUrl(filters.budget.value)}-max-${formatNumberForUrl(filters.budget.max)}`,
		);
	}

	// Bathrooms
	if (filters.bathroomsMin && filters.bathroomsMin > 1) {
		segments.push(`${filters.bathroomsMin}bathrooms`);
	}

	// Balconies
	if (filters.balconiesMin && filters.balconiesMin > 1) {
		segments.push(`${filters.balconiesMin}balconies`);
	}

	// Parkings
	if (filters.parkingsMin && filters.parkingsMin > 1) {
		segments.push(`${filters.parkingsMin}parkings`);
	}

	// Floors
	if (filters.floors && (filters.floors.min !== 1 || filters.floors.max !== 2)) {
		if (filters.floors.min === filters.floors.max) {
			segments.push(`floor-${filters.floors.min}`);
		} else {
			segments.push(`floors-${filters.floors.min}-${filters.floors.max}`);
		}
	}

	// Status
	if (filters.status) {
		segments.push(`status-${encodeValue(filters.status)}`);
	}

	// Water Sources
	if (filters.waterSources && filters.waterSources.length > 0) {
		const mappedStatus = filters.waterSources.map(encodeValue);
		segments.push(arrayToUrlSegment(mappedStatus, "watersources"));
	}

	// Level Type
	if (filters.levelType && filters.levelType.length > 0) {
		const mappedStatus = filters.levelType.map(encodeValue);
		segments.push(arrayToUrlSegment(mappedStatus, "level"));
	}

	// Furnishing
	if (filters.furnishing) {
		segments.push(`furnishing-${encodeValue(filters.furnishing)}`);
	}

	// Amenities (only include first few to keep URL reasonable)
	if (filters.amenities && filters.amenities.length > 0) {
		const topAmenities = filters.amenities.map(a => a.code).map(encodeValue);
		segments.push(arrayToUrlSegment(topAmenities, "amenities"));
	}

	// Documents
	if (filters.documents && filters.documents.length > 0) {
		segments.push(arrayToUrlSegment(filters.documents.map(encodeValue), "documents"));
	}

	// Spaces
	if (filters.spaces && filters.spaces.length > 0) {
		filters.spaces = filters.spaces.map(space => {
			// Convert "bathrooms" to "space_bathrooms" for URL because "bathrooms" is a reserved keyword
			if (space === "bathrooms") space = "space_bathrooms";
			return space;
		});
		const topSpaces = filters.spaces.map(encodeValue);
		segments.push(arrayToUrlSegment(topSpaces, "spaces"));
	}

	// Rental Value
	if (filters.rentalValue && filters.rentalValue.min && filters.rentalValue.max) {
		segments.push(rangeToUrlSegment(filters.rentalValue.min, filters.rentalValue.max, "rentalvalue"));
	}

	// Max Age
	if (filters.maxAge) {
		segments.push(`maxage-${encodeMaxAgeAndPossession(filters.maxAge)}`);
	}

	// Possession
	if (filters.possession) {
		segments.push(`possession-${encodeMaxAgeAndPossession(filters.possession)}`);
	}

	// Property Sub Type push at the end if it exists
	// Only add subtype to URL if it's NOT "any" (the default)
	if (subTypeLabel && subTypeLabel !== "any") {
		segments.push(`subtype-${subTypeLabel}`);
		if (filters.isCommunity) {
			segments.push(`community-yes`);
		}
	}

	// Note: coordinates and zoom are now handled as search parameters (center and zoom)
	// instead of being part of the pathname

	return segments.join("-");
}
